import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { API_BASE_URL } from '@/constants';
import { queryClient } from '@/lib/queryClient';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshTokenPromise: Promise<string> | null = null;

apiClient.interceptors.request.use(
  (config) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken && config.headers) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error?.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        useAuthStore.getState().setSessionExpired(true);
        return Promise.reject(error);
      }

      if (!refreshTokenPromise) {
        refreshTokenPromise = axios
          .post(`${API_BASE_URL}/auth/refresh`, { refreshToken })
          .then((response) => {
            const { accessToken, refreshToken: newRefreshToken } = response.data.data;

            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', newRefreshToken);
            useAuthStore.getState().setAccessToken(accessToken);

            queryClient.invalidateQueries();
            refreshTokenPromise = null;
            return accessToken;
          })
          .catch((refreshError) => {
            useAuthStore.getState().setSessionExpired(true);
            useAuthStore.getState().clearAuth();
            refreshTokenPromise = null;
            throw refreshError;
          });
      }

      try {
        const newAccessToken = await refreshTokenPromise;
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
